/**
 * @file src/pages/company-onboarding-wizard/components/Step1CompanyInfo.jsx
 * @description Wizard step 1 — company legal name, VAT number,
 * registration scheme ID, and optional logo upload.
 *
 * What changed from the previous version:
 *  - Select now uses options prop instead of children — your custom Select
 *    component (components/ui/Select.jsx) doesn't accept native <option>
 *    children. It expects { value, label }[] via the options prop.
 *  - onChange handler safely supports both event-style and value-style
 *    callbacks since Select implementations vary.
 */

import React, { useState, useMemo } from "react";
import { motion }                    from "framer-motion";
import { Shield, HelpCircle, CheckCircle, Upload, X, Image as ImageIcon } from "lucide-react";
import Input    from "components/ui/Input";
import Button   from "components/ui/Button";
import Select   from "components/ui/Select";
import {
  SELLER_SCHEME_IDS,
  LOGO_ACCEPTED_TYPES,
  LOGO_MAX_SIZE_BYTES,
} from "config/constants";

// ---------------------------------------------------------------------------
// Logo uploader
// ---------------------------------------------------------------------------

const LogoUploader = ({ logo, onUpload, onRemove }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [sizeError, setSizeError]   = useState(null);

  const processFile = (file) => {
    if (!file) return;
    setSizeError(null);

    if (!LOGO_ACCEPTED_TYPES.includes(file.type)) {
      setSizeError("Only PNG, JPG, and SVG files are accepted");
      return;
    }
    if (file.size > LOGO_MAX_SIZE_BYTES) {
      setSizeError("Logo must be under 5 MB");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => onUpload(reader.result);
    reader.readAsDataURL(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    processFile(e.dataTransfer.files?.[0]);
  };

  if (logo) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="border-2 border-gray-200 rounded-lg p-4 bg-gray-50"
      >
        <div className="flex items-center gap-4">
          <div className="relative w-20 h-20 shrink-0 bg-white rounded-lg border border-gray-200 overflow-hidden">
            <img src={logo} alt="Company logo preview" className="w-full h-full object-contain" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <ImageIcon className="w-4 h-4 text-gray-400" />
              <p className="text-sm font-medium text-gray-700 truncate">Logo uploaded</p>
            </div>
            <p className="text-xs text-gray-500 mt-1">Ready for use</p>
          </div>
          <button
            type="button"
            onClick={onRemove}
            aria-label="Remove logo"
            className="shrink-0 p-2 text-gray-400 hover:text-error hover:bg-error-50 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <div>
      <motion.div
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
        onDrop={handleDrop}
        whileHover={{ scale: 1.01 }}
        className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-all duration-200 cursor-pointer ${
          isDragging
            ? "border-primary bg-primary-50"
            : "border-gray-300 hover:border-primary hover:bg-gray-50"
        }`}
      >
        <input
          type="file"
          accept={LOGO_ACCEPTED_TYPES.join(",")}
          onChange={(e) => processFile(e.target.files?.[0])}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          aria-label="Upload company logo"
        />
        <div className="flex flex-col items-center gap-2 pointer-events-none">
          <div className={`p-3 rounded-full ${isDragging ? "bg-primary-100" : "bg-gray-100"}`}>
            <Upload className={`w-6 h-6 ${isDragging ? "text-primary" : "text-gray-400"}`} />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-700">
              {isDragging ? "Drop your logo here" : "Upload company logo"}
            </p>
            <p className="text-xs text-gray-500 mt-1">PNG, JPG, SVG up to 5 MB</p>
          </div>
        </div>
      </motion.div>

      {sizeError && <p className="text-sm text-error mt-1">{sizeError}</p>}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Step1CompanyInfo
// ---------------------------------------------------------------------------

const Step1CompanyInfo = ({ formData, updateFormData, errors, onNext }) => {
  const [showVatHelp, setShowVatHelp] = useState(false);

  const updateRegistrationNumber = (field, value) => {
    updateFormData("registrationNumber", {
      ...formData.registrationNumber,
      [field]: value,
    });
  };

  const isValid = useMemo(
    () =>
      formData.legalName?.trim() &&
      formData.vatNumber?.trim() &&
      formData.registrationNumber?.id?.trim() &&
      formData.registrationNumber?.schemeId?.trim(),
    [formData],
  );

  // ── Safe scheme ID change handler — supports event or direct value ──────
  const handleSchemeChange = (eventOrValue) => {
    const value =
      typeof eventOrValue === "string"
        ? eventOrValue
        : eventOrValue?.target?.value ?? eventOrValue?.value ?? "";
    updateRegistrationNumber("schemeId", value);
  };

  return (
    <div className="space-y-6">

      {/* ── Company Legal Name ─────────────────────────────────────────── */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Company Legal Name <span className="text-error">*</span>
        </label>
        <Input
          type="text"
          value={formData.legalName}
          onChange={(e) => updateFormData("legalName", e.target.value)}
          placeholder="Enter your company's legal name"
          className={errors.legalName ? "border-error" : ""}
          aria-describedby={errors.legalName ? "legalName-error" : undefined}
        />
        {errors.legalName && (
          <p id="legalName-error" className="text-sm text-error flex items-center gap-1">
            <HelpCircle className="w-4 h-4 shrink-0" />
            {errors.legalName}
          </p>
        )}
      </div>

      {/* ── VAT Number ────────────────────────────────────────────────── */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          VAT Number <span className="text-error">*</span>
          <button
            type="button"
            onClick={() => setShowVatHelp((v) => !v)}
            aria-label="VAT number format help"
            className="ml-1 text-primary hover:text-primary-600"
          >
            <HelpCircle className="w-4 h-4 inline" />
          </button>
        </label>

        {showVatHelp && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-primary-50 border border-primary-200 rounded-lg p-3 text-sm text-primary-700"
          >
            <div className="flex items-center gap-2 mb-1">
              <Shield className="w-4 h-4 text-primary shrink-0" />
              <span className="font-medium">VAT Format (BR-KSA-40)</span>
            </div>
            <ul className="text-xs space-y-0.5 ml-6">
              <li>• Exactly 15 digits</li>
              <li>• First digit must be 3</li>
              <li>• Last digit must be 3</li>
              <li>• Example: 3 1012345678 3</li>
            </ul>
          </motion.div>
        )}

        <div className="relative">
          <Input
            type="text"
            value={formData.vatNumber}
            onChange={(e) => updateFormData("vatNumber", e.target.value)}
            placeholder="310123456789003"
            maxLength={15}
            className={errors.vatNumber ? "border-error" : ""}
            aria-describedby={errors.vatNumber ? "vatNumber-error" : undefined}
          />
          {formData.vatNumber && /^3\d{13}3$/.test(formData.vatNumber) && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <CheckCircle className="w-5 h-5 text-success" />
            </div>
          )}
        </div>
        {errors.vatNumber && (
          <p id="vatNumber-error" className="text-sm text-error flex items-center gap-1">
            <HelpCircle className="w-4 h-4 shrink-0" />
            {errors.vatNumber}
          </p>
        )}
      </div>

      {/* ── Registration Number ───────────────────────────────────────── */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Registration Number <span className="text-error">*</span>
        </label>
        <Input
          type="text"
          value={formData.registrationNumber?.id ?? ""}
          onChange={(e) => updateRegistrationNumber("id", e.target.value)}
          placeholder="Enter your registration number"
          className={errors["registrationNumber.id"] ? "border-error" : ""}
          aria-describedby={errors["registrationNumber.id"] ? "regId-error" : undefined}
        />
        {errors["registrationNumber.id"] && (
          <p id="regId-error" className="text-sm text-error flex items-center gap-1">
            <HelpCircle className="w-4 h-4 shrink-0" />
            {errors["registrationNumber.id"]}
          </p>
        )}
      </div>

      {/* ── Registration Type / Scheme ID — uses options prop ────────── */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Registration Type <span className="text-error">*</span>
        </label>
        <Select
          value={formData.registrationNumber?.schemeId ?? ""}
          onChange={handleSchemeChange}
          options={SELLER_SCHEME_IDS}
          placeholder="Select registration type"
          className={errors["registrationNumber.schemeId"] ? "border-error" : ""}
          aria-describedby={errors["registrationNumber.schemeId"] ? "schemeId-error" : undefined}
        />
        {errors["registrationNumber.schemeId"] && (
          <p id="schemeId-error" className="text-sm text-error flex items-center gap-1">
            <HelpCircle className="w-4 h-4 shrink-0" />
            {errors["registrationNumber.schemeId"]}
          </p>
        )}
        <p className="text-xs text-gray-500">
          As per ZATCA BR-KSA-08 — select the type that matches your registration
        </p>
      </div>

      {/* ── Logo upload ───────────────────────────────────────────────── */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Company Logo
          <span className="text-gray-400 ml-1 font-normal">(Optional)</span>
        </label>
        <LogoUploader
          logo={formData.logo}
          onUpload={(base64) => updateFormData("logo", base64)}
          onRemove={() => updateFormData("logo", null)}
        />
      </div>

      {/* ── ZATCA compliance badge ────────────────────────────────────── */}
      <div className="bg-success-50 border border-success-200 rounded-lg p-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-success-100 rounded-lg shrink-0">
            <Shield className="w-5 h-5 text-success-600" />
          </div>
          <div>
            <h4 className="font-medium text-success-800">ZATCA Compliant</h4>
            <p className="text-sm text-success-700">
              This information is used for Phase 1 &amp; Phase 2 e-invoicing compliance.
            </p>
          </div>
        </div>
      </div>

      {/* ── Navigation ───────────────────────────────────────────────── */}
      <div className="flex justify-end pt-4">
        <Button onClick={onNext} disabled={!isValid} className="min-w-[120px]">
          Next Step →
        </Button>
      </div>

      <div className="text-center text-sm text-gray-500">
        Step 1 of 3 • Company Information
      </div>
    </div>
  );
};

export default Step1CompanyInfo;